import Storage from "../../services/storage-service/Storage";
import ErrorHandler from "../../utils/ErrorHandler";
import ManagerApiFeatureFlag from "../../utils/ManagerApiFeatureFlag";
import AuthService from "../auth-service/AuthService";
import AxiosService from "../axios-service/AxiosService";
import { EditUser, RedefinePassword, UserCreate } from "../models/User";
const managerBFF = process.env.REACT_APP_MANAGER_BFF_URL;

export default class UserService {
    static createUser = async (user: UserCreate) => {
        let token = null;
        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (e) {
            ErrorHandler.handleError(e);
        }
        const config = {
            headers: { Authorization: "Bearer " + token }
        };
        return await AxiosService.httpPost(
            managerBFF + "/v1/users",
            user,
            config
        );
    };

    static createOrganizationUser = async (user: UserCreate) => {
        let token = null;
        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (e) {
            ErrorHandler.handleError(e);
        }
        const config = {
            headers: { Authorization: "Bearer " + token }
        };
        return await AxiosService.httpPost(
            managerBFF + "/v1/users/organization",
            user,
            config
        );
    };

    static findUsers = async (): Promise<any> => {
        let token, config;
        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        config = {
            headers: {
                Authorization: "Bearer " + token,
                ...ManagerApiFeatureFlag.getNewManagerApiStorageConfig()
            }
        };
        return await AxiosService.httpGet(managerBFF + "/v1/users", config);
    };

    static findUsersByOrganizationId = async (): Promise<any> => {
        let token, config;
        let organizationData;
        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
            organizationData = Storage.get(Storage.KEYS.ORGANIZATION_DATA);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        if (organizationData) {
            const orgData = JSON.parse(organizationData);
            config = {
                headers: {
                    Authorization: "Bearer " + token,
                    ...ManagerApiFeatureFlag.getNewManagerApiStorageConfig()
                }
            };
            const resp = await AxiosService.httpGet(
                managerBFF + `/v1/users/organization/${orgData.id}`,
                config
            );
            return resp.data;
        }

        throw new Error("Não foi possível recuperar os usuários");
    };

    static findUsersByOrganizationIdV2 = async (
        limit: number,
        page?: number
    ): Promise<any> => {
        let token, config;
        let organizationData;
        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
            organizationData = Storage.get(Storage.KEYS.ORGANIZATION_DATA);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        if (organizationData) {
            const orgData = JSON.parse(organizationData);
            let params = {};
            let path = `/v1/users/organization`;

            config = {
                headers: {
                    Authorization: "Bearer " + token,
                    ...ManagerApiFeatureFlag.getNewManagerApiStorageConfig()
                },

                params: {
                    ...params,
                    ...{ organizationId: orgData.id, limit, page }
                }
            };

            const url = managerBFF + path;
            const resp = await AxiosService.httpGet(url, config);
            return resp.data;
        }

        throw new Error("Não foi possível recuperar os usuários");
    };

    static findOrganizationUserByEmail = async (
        email: string
    ): Promise<any> => {
        let token, config;
        let organizationData;
        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
            organizationData = Storage.get(Storage.KEYS.ORGANIZATION_DATA);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        if (organizationData) {
            const orgData = JSON.parse(organizationData);
            config = {
                headers: {
                    Authorization: "Bearer " + token,
                    ...ManagerApiFeatureFlag.getNewManagerApiStorageConfig()
                },

                params: {
                    email,
                    organizationId: orgData.id
                }
            };

            let path = "/v1/users/organization";

            const url = managerBFF + path;
            const resp = await AxiosService.httpGet(url, config);
            return resp.data;
        }

        throw new Error("Não foi possível recuperar os usuários");
    };

    static editUser = async (user: EditUser): Promise<any> => {
        let token, config;
        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        const organizationData = JSON.parse(
            Storage.get(Storage.KEYS.ORGANIZATION_DATA) || "{}"
        );

        const updatedUser = {
            developerEmail: user.email,
            developerRole: user.role,
            organizationId: organizationData.id,
            name: user.name
        };

        config = { headers: { Authorization: "Bearer " + token } };
        return await AxiosService.httpPut(
            managerBFF + "/v1/users/organization",
            updatedUser,
            config
        );
    };

    static redefinePassword = async (
        userInformations: RedefinePassword
    ): Promise<any> => {
        let token, config;
        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        config = { headers: { Authorization: "Bearer " + token } };
        return await AxiosService.httpPost(
            managerBFF + "/v1/users/password",
            userInformations,
            config
        );
    };

    static findMiniAppsFromUser = async (email: string): Promise<any> => {
        let token, config;
        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        config = { headers: { Authorization: "Bearer " + token } };
        return await AxiosService.httpGet(
            managerBFF + `/v1/users/${email}/miniapps`,
            config
        );
    };

    static findAllActions = async () => {
        let token, config;

        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        config = { headers: { Authorization: "Bearer " + token } };
        return await AxiosService.httpGet(
            managerBFF + "/v1/mini-apps/actions/list",
            config
        );
    };

    static findCheckedActions = async (uri: string) => {
        let token, config;

        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        config = { headers: { Authorization: "Bearer " + token } };
        if (uri.includes("http://")) uri = uri.replace("http", "https");
        const bffUri = uri.replace("api/p/", "bff/v1/mini-apps/actions/");
        return await AxiosService.httpGet(bffUri, config);
    };

    static editActions = async (
        user: string,
        actions: string[],
        slug: string
    ) => {
        let token, config;

        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        config = { headers: { Authorization: "Bearer " + token } };

        return await AxiosService.httpPut(
            managerBFF + `/v1/mini-apps/actions/users/${user}/miniapps/${slug}`,
            { actions },
            config
        );
    };

    static deleteCredential = async (userId: string, clientId: string) => {
        let token, config;

        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        config = { headers: { Authorization: "Bearer " + token } };
        return await AxiosService.httpDelete(
            managerBFF + `/v1/users/${userId}/credential/${clientId}`,
            config
        );
    };

    static createCredential = async (email: string) => {
        let token, config;

        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        config = { headers: { Authorization: "Bearer " + token } };
        return await AxiosService.httpPost(
            managerBFF + "/v1/users/create_credential",
            { email },
            config
        );
    };

    static readSelfUser = async () => {
        let token = null;

        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return await AxiosService.httpGet(`${managerBFF}/v1/users/me`, config);
    };

    static createSelfCredential = async () => {
        let token = null;

        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return await AxiosService.httpPost(
            `${managerBFF}/v1/users/me/credential`,
            {},
            config
        );
    };

    static generateCredential = async (payload: any) => {
        let token = null;

        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return await AxiosService.httpPost(
            `${managerBFF}/v1/users/me/generate-credential`,
            payload,
            config
        );
    };

    static deleteSelfCredential = async (clientId: string) => {
        let token = null;

        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return await AxiosService.httpDelete(
            `${managerBFF}/v1/users/me/credentials/${clientId}`,
            config
        );
    };

    static deleteMultipleCredentials = async (payload: string) => {
        let token = null;

        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };
        console.log({ token, payload });

        return await AxiosService.httpPatch(
            `${managerBFF}/v1/users/me/delete/multiple-credentials`,
            payload,
            config
        );
    };

    static findActionsBySlug = async (slug: string) => {
        let token = null;

        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (error) {
            ErrorHandler.handleError(error);
        }

        const config = {
            headers: { Authorization: "Bearer " + token }
        };
        const email = AuthService.getDecodedToken().email;
        const url = `${managerBFF}/v1/mini-apps/actions/users/${email}/miniapps/${slug}`;

        return await AxiosService.httpGet(url, config);
    };
}
